import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  Tooltip,
  Grid,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Visibility as VisibilityIcon, AccountBalanceWallet as AccountBalanceWalletIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import notaCreditoCompraService, {
  NotaCreditoCompra,
  NotaCreditoCompraSearchParams
} from '../../services/notaCreditoCompraService';
import { Proveedor } from '../../services/proveedorService';
import ProveedorSearch from '../../components/ProveedorSearch';
import AplicarSaldoReciboDialog from '../../components/Tesoreria/AplicarSaldoReciboDialog';
import { formatCurrency, formatDateLocal, toLocalDateStringYMD } from '../../utils/formatters';

const getFirstDayOfMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  return toLocalDateStringYMD(d);
};

const getLastDayOfMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return toLocalDateStringYMD(d);
};

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const s = formatDateLocal(fecha);
  return s || '-';
};

const formatoComprobante = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

const NotasCreditoCompra: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [notas, setNotas] = useState<NotaCreditoCompra[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [nombreProveedorFiltro, setNombreProveedorFiltro] = useState('');
  const [aplicarDialogOpen, setAplicarDialogOpen] = useState(false);
  const [aplicarDialogNc, setAplicarDialogNc] = useState<NotaCreditoCompra | null>(null);
  const [filters, setFilters] = useState({
    searchTerm: '',
    fechaDesde: getFirstDayOfMonth(),
    fechaHasta: getLastDayOfMonth(),
    nroProveedor: '',
    letra: '',
    nroSuc: '',
    nroComprobante: ''
  });

  const fetchNotas = useCallback(async (
    pageNum: number = page,
    pageSize: number = rowsPerPage,
    filtros: typeof filters = filters
  ) => {
    try {
      setLoading(true);
      const params: NotaCreditoCompraSearchParams = {
        pageNumber: pageNum + 1,
        pageSize,
        searchTerm: filtros.searchTerm || undefined,
        fechaDesde: filtros.fechaDesde ? new Date(filtros.fechaDesde) : undefined,
        fechaHasta: filtros.fechaHasta ? new Date(filtros.fechaHasta) : undefined,
        nroProveedor: filtros.nroProveedor || undefined,
        letra: filtros.letra || undefined,
        nroSuc: filtros.nroSuc ? parseInt(filtros.nroSuc, 10) : undefined,
        nroComprobante: filtros.nroComprobante ? parseInt(filtros.nroComprobante, 10) : undefined
      };
      const result = await notaCreditoCompraService.getNotasCreditoCompra(params);
      setNotas(result.items || []);
      setTotalCount(result.totalCount || 0);
    } catch (error: unknown) {
      console.error('Error al cargar notas de crédito de compra:', error);
      enqueueSnackbar('Error al cargar notas de crédito de compra', { variant: 'error' });
      setNotas([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, filters, page, rowsPerPage]);

  useEffect(() => {
    fetchNotas();
  }, [fetchNotas]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchNotas(0, rowsPerPage, filters);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [filters, fetchNotas, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchNotas(newPage, rowsPerPage, filters);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchNotas(0, newRowsPerPage, filters);
  };

  const handleProveedorSelect = (proveedor: Proveedor) => {
    setFilters((f) => ({ ...f, nroProveedor: String(proveedor.nroProveedor) }));
    setNombreProveedorFiltro((proveedor.nombre ?? '').trim());
  };

  const handleAbrirAplicarSaldo = (nc: NotaCreditoCompra) => {
    if (!nc.nroProveedor || nc.nroProveedor <= 0) {
      enqueueSnackbar('La nota de crédito no tiene proveedor asociado.', { variant: 'warning' });
      return;
    }
    setAplicarDialogNc(nc);
    setAplicarDialogOpen(true);
  };

  const handleCloseAplicarDialog = () => {
    setAplicarDialogOpen(false);
    setAplicarDialogNc(null);
  };

  const sxView = {
    bgcolor: '#0284c7',
    color: '#fff',
    borderRadius: 1.5,
    '&:hover': { bgcolor: '#0369a1', transform: 'scale(1.05)' },
    transition: 'all 0.2s',
  };

  const sxWallet = {
    bgcolor: '#2e7d32',
    color: '#fff',
    borderRadius: 1.5,
    '&:hover': { bgcolor: '#1b5e20', transform: 'scale(1.1)' },
    transition: 'all 0.2s',
  };

  const renderAccionesNc = (nc: NotaCreditoCompra) => (
    <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
      <Tooltip title="Ver">
        <IconButton
          size="small"
          onClick={() => navigate(`/compras/notas-credito/${nc.nroFormulario}`)}
          sx={sxView}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {nc.nroProveedor != null && nc.nroProveedor > 0 && (
        <Tooltip title="Aplicar saldo / Ver aplicaciones">
          <IconButton size="small" onClick={() => handleAbrirAplicarSaldo(nc)} sx={sxWallet}>
            <AccountBalanceWalletIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Notas de Crédito de Compra
            </Typography>
            <Button
              variant="contained"
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
              startIcon={<AddIcon />}
              onClick={() => navigate('/compras/notas-credito/nuevo')}
            >
              Nueva Nota de Crédito
            </Button>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar..."
                value={filters.searchTerm}
                onChange={(e) => setFilters(f => ({ ...f, searchTerm: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#1976d2' }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha desde"
                value={filters.fechaDesde}
                onChange={(e) => setFilters(f => ({ ...f, fechaDesde: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha hasta"
                value={filters.fechaHasta}
                onChange={(e) => setFilters(f => ({ ...f, fechaHasta: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Letra"
                value={filters.letra}
                onChange={(e) => setFilters(f => ({ ...f, letra: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Suc"
                value={filters.nroSuc}
                onChange={(e) => setFilters(f => ({ ...f, nroSuc: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Nro. Comp."
                value={filters.nroComprobante}
                onChange={(e) => setFilters(f => ({ ...f, nroComprobante: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Nro. proveedor"
                placeholder="Ej: 1001"
                value={filters.nroProveedor}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  setFilters((f) => ({ ...f, nroProveedor: v }));
                  setNombreProveedorFiltro('');
                }}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Número de proveedor' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Buscar proveedor por nombre">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setOpenProveedorSearch(true)}
                          aria-label="Buscar proveedor por nombre"
                          sx={{ color: '#1976d2' }}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                helperText={nombreProveedorFiltro ? `Nombre: ${nombreProveedorFiltro}` : undefined}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress sx={{ color: '#1976d2' }} />
            </Box>
          ) : isMobile ? (
            <>
              <Stack spacing={2}>
                {notas.map((nc) => (
                  <Card key={nc.nroFormulario} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {formatoComprobante(nc.letra, nc.nroSuc, nc.nroComprobante ?? undefined)} - {nc.nombre || 'Sin nombre'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Nro. Formulario: {nc.nroFormulario} | Fecha: {formatearFecha(nc.fechaEmision)} | Total: {formatCurrency(nc.importeTotal ?? 0)}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {renderAccionesNc(nc)}
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas:"
              />
            </>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. Formulario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notas.map((nc) => (
                    <TableRow key={nc.nroFormulario} hover>
                      <TableCell>{nc.nroFormulario}</TableCell>
                      <TableCell>{formatearFecha(nc.fechaEmision)}</TableCell>
                      <TableCell>{formatoComprobante(nc.letra, nc.nroSuc, nc.nroComprobante ?? undefined)}</TableCell>
                      <TableCell>{nc.nombre || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(nc.importeTotal ?? 0)}</TableCell>
                      <TableCell align="center">{renderAccionesNc(nc)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas por página:"
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ProveedorSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={handleProveedorSelect}
      />

      {aplicarDialogNc && aplicarDialogNc.nroProveedor != null && aplicarDialogNc.nroProveedor > 0 && (
        <AplicarSaldoReciboDialog
          open={aplicarDialogOpen}
          onClose={handleCloseAplicarDialog}
          reciboId={aplicarDialogNc.nroFormulario}
          nroCliente={aplicarDialogNc.nroProveedor}
          nombreCliente={aplicarDialogNc.nombre || `Proveedor ${aplicarDialogNc.nroProveedor}`}
          numeroComprobanteCredito={formatoComprobante(
            aplicarDialogNc.letra,
            aplicarDialogNc.nroSuc,
            aplicarDialogNc.nroComprobante ?? undefined
          )}
          variant="proveedor"
          nroTipoFormularioCredito={aplicarDialogNc.nroTipoFormulario ?? '3'}
          importeTotalCredito={aplicarDialogNc.importeTotal ?? undefined}
          onSuccess={() => fetchNotas(page, rowsPerPage, filters)}
        />
      )}
    </Box>
  );
};

export default NotasCreditoCompra;
